import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaPhone, FaLinkedin, FaGithub, FaPaperPlane, FaDownload } from "react-icons/fa";
import emailjs from "@emailjs/browser";

const ContactInfo = ({ icon: Icon, label, value, link }) => (
  <motion.a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    whileHover={{ x: 5 }}
    className="flex items-center gap-4 p-4 rounded-2xl hover:bg-white/5 transition-colors group"
  >
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent/20 to-neon-purple/20 flex items-center justify-center text-accent group-hover:scale-110 transition-transform">
      <Icon size={20} />
    </div>
    <div>
      <p className="text-sm text-slate-400">{label}</p>
      <p className="text-white font-medium group-hover:text-accent transition-colors">{value}</p>
    </div>
  </motion.a>
);

export default function Contact() {
  const formRef = useRef();
  const [formState, setFormState] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const SERVICE_ID = "service_dk4wze7";
    const TEMPLATE_ID = "template_m2f9zki";
    const PUBLIC_KEY = "hIE50mBhWOEHzz-Bt";

    emailjs
      .sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, PUBLIC_KEY)
      .then(
        () => {
          setIsSubmitting(false);
          alert("Message sent successfully!");
          setFormState({ name: "", email: "", message: "" });
        },
        (error) => {
          setIsSubmitting(false);
          console.error("FAILED...", error.text);
          alert(`Failed to send message: ${error.text}`);
        }
      );
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-4 md:px-6">
      <div className="section-container max-w-6xl">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl font-bold mb-6">
              Let's <span className="text-accent">Connect</span>
            </h2>
            <p className="text-slate-400 text-lg mb-12 leading-relaxed">
              I'm always open to discussing new projects, creative ideas, or opportunities to be part of your visions.
            </p>

            <div className="space-y-6 mb-12">
              <ContactInfo
                icon={FaEnvelope}
                label="Email"
                value="alihasanshah755@gmail.com"
                link="mailto:alihasanshah755@gmail.com"
              />
              <ContactInfo
                icon={FaPhone}
                label="Phone"
                value="+92 348 0090755"
                link="tel:+923480090755"
              />
              <ContactInfo
                icon={FaLinkedin}
                label="LinkedIn"
                value="sayed-hasan-ali-shah"
                link="https://www.linkedin.com/in/sayed-hasan-ali-shah"
              />
              <ContactInfo
                icon={FaGithub}
                label="GitHub"
                value="SayedHasanAliShah"
                link="https://github.com/SayedHasanAliShah"
              />
            </div>

            <div className="glass-card mb-8">
              <h3 className="font-bold text-lg mb-4 text-accent">Experience Letters</h3>
              <div className="flex flex-col gap-3">
                <a
                  href="https://drive.google.com/drive/folders/1zTAADMmlP4ENvGhp_YX0KRjceIiyWKS9?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors"
                >
                  <FaDownload size={16} />
                  <span>Olumi Tech - Nov 2025 to Apr 2026</span>
                </a>
                <a
                  href="https://drive.google.com/drive/folders/1v7NUnb_X6vzTitTUWYRVEXpXs_fP-4yM?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors"
                >
                  <FaDownload size={16} />
                  <span>Senew Tech - Jan 2025 to Nov 2025</span>
                </a>
              </div>
            </div>

            <div className="glass-card">
              <h3 className="font-bold text-lg mb-4 text-accent">Published Apps</h3>
              <div className="flex flex-col gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.runningtribe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors"
                >
                  <FaDownload size={16} />
                  <span>RunningTribe - Google Play</span>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.sabkobol&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors"
                >
                  <FaDownload size={16} />
                  <span>Sabkobol - Google Play</span>
                </a>
                <a
                  href="https://apps.apple.com/us/app/sabkobol/id6751508730"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors"
                >
                  <FaDownload size={16} />
                  <span>Sabkobol - App Store</span>
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="glass-card relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-[80px] pointer-events-none" />

            <form ref={formRef} onSubmit={handleSubmit} className="relative z-10 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Name</label>
                <input
                  type="text"
                  name="user_name"
                  required
                  value={formState.name}
                  onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-white placeholder-slate-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Email</label>
                <input
                  type="email"
                  name="user_email"
                  required
                  value={formState.email}
                  onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-white placeholder-slate-500"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Message</label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  value={formState.message}
                  onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-white placeholder-slate-500 resize-none"
                  placeholder="Tell me about your project..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center gap-2 group"
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                ) : (
                  <>
                    Send Message
                    <FaPaperPlane className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
